import React from 'react';
import { TrendingUp, Wallet, Users, FileStack } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { useData } from '../context/DataContext';
import { computeTotals, fmtRM, fmtRM2 } from '../lib/store';
import { PageHeader, Card } from '../components/Shared';

const PIE_COLORS = ['#2563eb', '#8b5cf6', '#ec4899', '#f97316'];

export default function Reports() {
  const { invoices, quotations, receipts, vouchers, customers, company } = useData();

  const totalSales = invoices.reduce((s, i) => s + computeTotals(i.items, company.sstRate).total, 0);
  const totalCollected = receipts.reduce((s, r) => s + r.amount, 0);
  const totalOutstanding = customers.reduce((s, c) => s + (c.outstanding || 0), 0);
  const totalExpenses = vouchers.reduce((s, v) => s + v.amount, 0);

  const docMix = [
    { name: 'Quotations', value: quotations.length },
    { name: 'Invoices', value: invoices.length },
    { name: 'Receipts', value: receipts.length },
    { name: 'Vouchers', value: vouchers.length },
  ];

  const customerBalances = customers
    .filter((c) => c.outstanding > 0)
    .sort((a, b) => b.outstanding - a.outstanding)
    .map((c) => ({ name: c.name.length > 14 ? c.name.slice(0, 12) + '…' : c.name, outstanding: c.outstanding }));

  const stats = [
    { label: 'Total Sales', value: fmtRM(totalSales), icon: TrendingUp, tint: 'bg-blue-100 text-blue-600' },
    { label: 'Collected', value: fmtRM(totalCollected), icon: Wallet, tint: 'bg-emerald-100 text-emerald-600' },
    { label: 'Outstanding', value: fmtRM(totalOutstanding), icon: Users, tint: 'bg-orange-100 text-orange-600' },
    { label: 'Expenses (Vouchers)', value: fmtRM(totalExpenses), icon: FileStack, tint: 'bg-violet-100 text-violet-600' },
  ];

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Reports" subtitle="Sales, outstanding, collections and more — all in one place." />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5">
            <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-lg ${s.tint}`}><s.icon className="h-5 w-5" /></div>
            <p className="text-xs font-medium text-slate-500">{s.label}</p>
            <p className="text-xl font-bold text-slate-900">{s.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h2 className="mb-4 font-semibold text-slate-800">Outstanding by Customer</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={customerBalances} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtRM2(v)} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Bar dataKey="outstanding" name="Outstanding" fill="#f97316" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 font-semibold text-slate-800">Document Mix</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={docMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {docMix.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
